/**
 * NotificationBell — the "What's new" feed. Clicking the bell opens a dropdown
 * of the rolled-up alerts from useNavAlerts, each deep-linking to its page.
 * Hovering a row calls onHighlight(domainKey) so the parent can glow the nav
 * domain that is the SOURCE of that notification. Alert keys map to a label +
 * destination here; the owning domain is derived from navTree.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { NavAlerts } from './useNavAlerts';
import { domainKeyForNotif } from '../navigation/navTree';
import { Icon } from './navIcons';
import { NavDot } from './NavDot';

const ALERT_META: Record<string, { label: string; to: string; icon: string }> = {
  receipts: { label: 'Receipts awaiting your review', to: '/receipts', icon: 'fin' },
  monday: { label: 'Monday imports to review', to: '/events/monday-inbox', icon: 'shows' },
  transfers: { label: 'Drone transfers awaiting your decision', to: '/customers/transfers', icon: 'transfer' },
  rma: { label: 'RMAs pending review', to: '/rma', icon: 'rma' },
  // Deep-links to the tab that HAS the Return button, not just to the page. A
  // count with no row here badges the nav item but is invisible in the bell, so
  // every new notif key must land in this map or it half-ships.
  logistics: { label: 'Shows awaiting gear return', to: '/dispatch?direction=in_transit', icon: 'dispatch' },
  // 'receiving' was removed 2026-07-25 (a permanently-lit badge is furniture;
  // useNavAlerts carries the reasoning) — its row here outlived the count that
  // fed it. 'approvals' is the opposite shape: empty in steady state, and only
  // approvers ever see a number, because the endpoint answers 0 to everyone else.
  approvals: { label: 'Purchase orders awaiting approval', to: '/procurement?tab=pending_approval', icon: 'order' },
  lowparts: { label: 'SKUs at or below reorder point', to: '/inventory', icon: 'box' },
  // As with requisitions, the nav row's `workOrders` count is the sum of these two and has no line here.
  workOrderInbox: { label: 'Work orders waiting for approval', to: '/work-orders?tab=inbox', icon: 'order' },
  workOrderPicks: { label: 'Boxes requested from the line', to: '/pick-list', icon: 'order' },
  // The nav row's `requisitions` count is the sum of these two and has no line
  // here on purpose, so the bell total does not count a request twice.
  requisitionApprovals: { label: 'Requests waiting on your approval', to: '/requisitions?tab=pending', icon: 'request' },
  requisitionsToBuy: { label: 'Requests ready to buy', to: '/requisitions?tab=to_buy', icon: 'request' },
};

interface NotificationBellProps {
  alerts: NavAlerts;
  onHighlight?: (domainKey: string | null) => void;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ alerts, onHighlight }) => {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const clearHighlight = () => onHighlight?.(null);
  const close = () => {
    setOpen(false);
    clearHighlight();
  };

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const entries = Object.entries(alerts).filter(([k, c]) => c > 0 && ALERT_META[k]);
  const total = entries.reduce((a, [, c]) => a + c, 0);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => (open ? close() : setOpen(true))}
        title={total > 0 ? `${total} items need attention` : 'Notifications'}
        aria-label={total > 0 ? `Notifications, ${total} pending` : 'Notifications'}
        aria-haspopup="menu"
        aria-expanded={open}
        className="relative w-8 h-8 rounded-md inline-flex items-center justify-center text-on-surface-variant hover:text-white hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
      >
        <Icon name="bell" strokeWidth={1.5} className="w-[17px] h-[17px]" />
        {total > 0 && <NavDot pulse className="absolute top-1 right-1.5 ring-2 ring-[#191919]" />}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full right-0 mt-2 z-50 w-[264px] bg-[#242424] border border-gray-700/60 rounded-xl shadow-2xl shadow-black/50 overflow-hidden"
          onMouseLeave={clearHighlight}
        >
          <div className="px-3.5 py-2.5 border-b border-gray-800 text-[11px] tracking-wider uppercase text-on-surface-variant font-semibold">
            Notifications
          </div>
          {entries.length === 0 ? (
            <div className="px-3.5 py-6 text-center text-[13px] text-on-surface-variant">You&rsquo;re all caught up.</div>
          ) : (
            <div className="py-1.5">
              {entries.map(([k, c]) => {
                const m = ALERT_META[k];
                const domain = domainKeyForNotif(k);
                return (
                  <button
                    key={k}
                    onMouseEnter={() => onHighlight?.(domain)}
                    onFocus={() => onHighlight?.(domain)}
                    onClick={() => {
                      history.push(m.to);
                      close();
                    }}
                    className="group w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-[#323232] transition-colors"
                  >
                    <Icon name={m.icon} className="w-4 h-4 text-on-surface-variant group-hover:text-white shrink-0" />
                    <span className="flex-1 text-[13px] text-on-surface group-hover:text-white">{m.label}</span>
                    <NavDot count={c} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
